import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Tooltip,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Pagination,
  useTheme,
  IconButton
} from '@mui/material';
import { useSelector } from 'react-redux';
import { Vehicle } from '../state/types';
import { State } from '../state/reducers';
import VehiclesTable from '../components/vehicles/VehiclesTable';
import FiltersSidebar, { defaultFilters } from '../components/vehicles/FiltersSidebar';
import VehicleOperations from '../components/vehicles/VehicleOperations';
import GenerateCsvModal from '../components/csv/GenerateCsvModal';
import SyncIcon from '@mui/icons-material/Sync';
import { getVehiclesFromDb } from '../api/getVehiclesFromDb';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import RoleBasedAccess from '../services/RoleBasedAccess';
import PaginationFooter from '../components/vehicles/PaginationFooter';

const Vehicles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const loadInitialState = () => {
    const stored = sessionStorage.getItem('vehiclesNavState');
    if (!stored) return { page: 1, filters: {}, resultsPerPage: 10 };
    try {
      const parsed = JSON.parse(stored);
      return {
        page: parsed.page || 1,
        filters: parsed.filters || {},
        resultsPerPage: parsed.resultsPerPage || 10,
      };
    } catch {
      return { page: 1, filters: {}, resultsPerPage: 10 };
    }
  };

  const { page: initialPage, filters: initialFilters, resultsPerPage: initialRPP } = loadInitialState();

  const [page, setPage] = useState(initialPage);
  const [filters, setFilters] = useState(initialFilters);
  const [resultsPerPage, setResultsPerPage] = useState(initialRPP);
  const selectedSystem = useSelector((state: State) => state.systemState.selectedSystem);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [postDateSortDirection, setPostDateSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const savedState = { page, filters, resultsPerPage };
    sessionStorage.setItem('vehiclesNavState', JSON.stringify(savedState));
  }, [page, filters, resultsPerPage]);

  const getVehicles = async () => {
    try {
      setLoadingVehicles(true);
      const body = {
        ...filters,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem,
        sort_direction: postDateSortDirection,
      };
      const { vehicles: translatedVehicles, total } = await getVehiclesFromDb(body);
      setVehicles(translatedVehicles);
      setTotalVehicles(total);
      setSelectedVehicles([]);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      getVehicles();
    }, 150);
    return () => clearTimeout(timeout);
  }, [selectedSystem, filters, page, resultsPerPage, postDateSortDirection]);

  const updateFilters = (newFilters: any) => {
    setPage(1);
    setFilters(newFilters);
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        setPollingEnabled(false);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!pollingEnabled) return;
    getVehicles();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        getVehicles();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [pollingEnabled]);

  const filtersApplied =
    Object.keys(filters).length > 0 &&
    JSON.stringify(filters) !== JSON.stringify(defaultFilters);

  const sidebarWidth = isSidebarOpen
    ? (isMobile ? '70vw' : '280px')
    : (isMobile ? '12vw' : '60px');

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 64px)', // 64px = Navbar height
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          width: sidebarWidth,
          transition: 'width 0.3s ease-in-out',
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.text.primary,
          display: 'flex',
          flexDirection: 'column',
          px: isSidebarOpen ? 2 : 0,
          zIndex: 100,
          flexShrink: 0
        }}
      >
        {isSidebarOpen ? (
          <FiltersSidebar
            toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
            setFilters={updateFilters}
          />
        ) : (
          <Box display="flex" flexDirection="column">
            <Tooltip title="Open Filters" placement="right" arrow>
              <IconButton
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                sx={{
                  m: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 5,
                  color: filtersApplied ? '#fff' : theme.palette.text.primary,
                  backgroundColor: filtersApplied ? 'darkred' : 'transparent',
                  '&:hover': {
                    backgroundColor: filtersApplied ? '#a30000' : theme.palette.action.hover,
                    borderColor: filtersApplied ? '#a30000' : theme.palette.text.primary,
                  },
                }}
              >
                <FilterAltOutlinedIcon />
              </IconButton>
            </Tooltip>

            <GenerateCsvModal />

            <Tooltip title="Poll for new vehicles every 60 seconds" placement="right" arrow>
              <IconButton
                onClick={() => setPollingEnabled((prev) => !prev)}
                sx={{
                  m: 1,
                  borderRadius: 5,
                  border: `1px solid ${theme.palette.divider}`,
                  color: pollingEnabled ? '#fff' : theme.palette.text.primary,
                  backgroundColor: pollingEnabled ? 'darkred' : 'transparent',
                  '&:hover': {
                    backgroundColor: pollingEnabled ? '#a30000' : theme.palette.action.hover,
                    borderColor: pollingEnabled ? '#a30000' : theme.palette.text.primary,
                  },
                }}
              >
                {pollingEnabled ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <SyncIcon />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: `calc(100vw - ${sidebarWidth})`,
          padding: 1,
          color: 'text.primary',
          overflow: 'hidden',
        }}
      >

        {loadingVehicles ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '50vh',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{
              flex: 1,
              overflowY: 'auto',
              paddingBottom: '20px',
              scrollbarWidth: 'none',        // Firefox
              '&::-webkit-scrollbar': {
                display: 'none',             // Chrome, Safari
              },
            }}>
                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <VehicleOperations
                    selectedVehicles={selectedVehicles}
                    onVehiclesUpdate={getVehicles}
                  />
                </RoleBasedAccess>
                <VehiclesTable
                  vehicles={vehicles}
                  setSelectedVehicles={setSelectedVehicles}
                  isMobile={isMobile}
                  postDateSortDirection={postDateSortDirection}
                  setPostDateSortDirection={setPostDateSortDirection}
                />
            </Box>

            {/* Pagination + Results Per Page */}
            <PaginationFooter
              totalVehicles={totalVehicles}
              currentPage={page}
              onPageChange={(_, value) => setPage(value)}
              rowsPerPage={resultsPerPage}
              onRowsPerPageChange={(e) => setResultsPerPage(Number(e.target.value))}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default Vehicles;
